import React, { FC } from 'react';
import { ArrowRightIcon, LinkIcon } from '@heroicons/react/24/solid';
import Link from '@/components/atoms/link';
import Typography from '@/components/atoms/typography';
import Markdown from '@/components/atoms/markdown';
import Button from '@/components/atoms/button';
import useClassNames from '../../hooks/useClassNames';
import Accordion from '@/components/molecules/accordion';
import { ArrowDownIcon } from '@heroicons/react/24/outline';
import useTranslation from '@/providers/I18n/hooks/useTranslation';
import { useStoreAndBusinessUnits } from '@/providers/store-and-business-units';
import { useParams } from 'next/navigation';

export interface Props {
  link: any;
  categoriesNavigator?: any[];
  onClick: () => void;
  hideHeaderMenu: () => void;
}

const MobileMenuNavButton: FC<Props> = ({ link, categoriesNavigator, onClick, hideHeaderMenu }) => {
  const { locale } = useParams();
  const linkClassNames = useClassNames(['flex justify-between border-b-2 border-white px-[48px] py-[14px]']);
  const { selectedBusinessUnit } = useStoreAndBusinessUnits();
  const { translate } = useTranslation();
  return (
    <div key={link?.label} className="cursor-pointer">
      {link?.mega_menu?.submenu_links && link?.mega_menu?.submenu_links?.length > 0 ? (
        <div onClick={onClick} className={linkClassNames}>
          <Typography className=" text-primary-black text-20 font-semibold">{link?.label}</Typography>
          <div>
            <svg width="18" height="19" viewBox="0 0 18 19" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15.2863 6.35937L9.00056 12.6451L2.71484 6.35938"
                stroke="#090909"
                strokeWidth="1.57143"
                strokeLinecap="square"
              />
            </svg>
          </div>
        </div>
      ) : (
        <div className="flex flex-col">
          {link?.title?.length > 0 ? (
            <>
              <Accordion className="border-b border-white">
                {link.title === 'By Segment' ? (
                  <Accordion.Button className="!justify-start gap-10 px-[48px] py-[12px] text-left text-20 font-semibold text-[#090909] [&>p]:text-20 [&>p]:font-medium [&>p]:leading-[21px]">
                    {link?.title}
                    <>
                      <Button
                        size="s"
                        className=" h[30px]  relative ml-2 w-[100px] !rounded-full !bg-[#FF0000] !px-1 !text-12 "
                      >
                        {translate('common.coming.soon.button')}
                      </Button>
                    </>
                  </Accordion.Button>
                ) : (
                  <Accordion.Button className="!justify-start gap-10 px-[48px] py-[12px] text-left text-20 font-semibold text-[#090909] [&>p]:text-20 [&>p]:font-medium [&>p]:leading-[21px]">
                    {link?.title}
                  </Accordion.Button>
                )}

                <Accordion.Panel className="-mb-[25px] !px-0 text-20 font-medium text-[#090909]">
                  {link?.mega_menu &&
                    link?.mega_menu?.map((field: any, index: number, array: any) => (
                        <Link
                          key={index}
                          link={`${field?.mega_menu_url?.href}`}
                          onClick={hideHeaderMenu}
                        >
                          <Markdown
                            className={`${index !== array?.length - 1 && 'border-b-2'
                              } text-primary-black border-white px-[48px] py-[14px] text-16 font-medium leading-[21px] [&>div>img]:hidden [&>figure]:hidden [&>img]:hidden [&>p>a>img]:hidden [&>p]:text-16 [&>p]:font-medium [&>p]:leading-[21px] [&>p]:text-[#090909]`}
                          >
                            {field?.mega_menu_content || field?.mega_menu_url?.title}
                          </Markdown>
                        </Link>
                      ))}
                  {link?.ctas &&
                    link?.ctas.map((cta: any, index: number) => (
                      <Link link={cta?.button?.href} key={index} onClick={hideHeaderMenu}>
                        <Button
                          Icon={ArrowRightIcon}
                          iconPosition="right"
                          className="w-full bg-inherit px-[48px] py-[14px] "
                        >
                          <span className="text-16 font-medium text-[#090909] underline underline-offset-4">
                            {cta?.button?.title}
                          </span>
                        </Button>
                      </Link>
                    ))}
                </Accordion.Panel>
              </Accordion>
            </>
          ) : (
            <>
              {link?.title?.length < 1 &&
                link?.mega_menu?.map((section: any, index: number) => {
                  if (section?.mega_menu_url?.title?.length > 0) {
                    const isAbsoluteUrl = section?.mega_menu_url?.href.startsWith('http');
                    const linkHref = isAbsoluteUrl
                      ? section?.mega_menu_url?.href
                      : `/${locale}${section?.mega_menu_url?.href}`;
                    const isPricelist = section?.mega_menu_url?.title === 'Product Pricelist';
                    const content = (
                      <Markdown
                        key={index}
                        className="text-primary-black border-b-2 border-white px-[48px] py-[14px] text-16 font-medium leading-[21px] [&>div>img]:hidden [&>figure]:hidden [&>img]:hidden [&>p>a>img]:hidden [&>p]:text-16 [&>p]:font-medium [&>p]:text-[#090909]"
                      >
                        {section?.mega_menu_url?.title}
                      </Markdown>
                    );
                    return (
                      !isPricelist && (
                        <div key={index} className="w-full">

                          <Link
                            link={`${linkHref}`}
                            target={isAbsoluteUrl ? '_blank' : undefined}
                            onClick={hideHeaderMenu}
                          >
                            {content}
                          </Link>

                        </div>
                      )
                    );
                  } else if (section?.mega_menu_content?.length > 0) {
                    return (
                      <div key={index} className="w-full">
                        <Markdown
                          key={index}
                          className="text-primary-black border-b-2 border-white px-[48px] py-[14px] text-16 font-medium leading-[21px] [&>div>img]:hidden [&>figure]:hidden [&>img]:hidden [&>p>a>img]:hidden [&>p]:text-16 [&>p]:font-medium [&>p]:text-[#090909]"
                        >
                          {section.mega_menu_content || section?.mega_menu_url?.title}
                        </Markdown>
                      </div>
                    );
                  }
                  return null;
                })}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default MobileMenuNavButton;